import React, { useEffect, useRef, useState } from 'react';

import StatusPill from '../../shared/status_pill';
import RunItems from './run_items';
import { reason_error } from '../params';
import { takeover_confirm_text } from './adapters';

const RunPanel = ({
  run,
  bucket,
  offset,
  items_loading = false,
  poll_failed = false,
  action_busy = '',
  action_error = '',
  focus_token = 0,
  focused_token = 0,
  on_focused,
  on_bucket,
  on_page,
  on_cancel,
  on_resume,
  on_retry,
  on_view_issues,
  on_dismiss
}) => {
  const heading_ref = useRef(null);
  const [stop_open, setStopOpen] = useState(false);
  const [stop_reason, setStopReason] = useState('');
  const [stop_touched, setStopTouched] = useState(false);
  const [retry_busy, setRetryBusy] = useState(false);
  const [retry_text, setRetryText] = useState('');
  // "Resume as you" hands every remaining teacher to the viewer's name, so it is confirmed first.
  const [takeover_open, setTakeoverOpen] = useState(false);
  const takeover_ref = useRef(null);
  const resume_ref = useRef(null);
  const stop_ref = useRef(null);
  const stop_button_ref = useRef(null);
  // The stop form unmounts the control that had focus; it goes back to Stop, or to the heading.
  const return_focus = useRef(null);

  useEffect(() => {
    if (!focus_token || focus_token === focused_token || !heading_ref.current) return;
    heading_ref.current.focus({ preventScroll: false });
    if (on_focused) on_focused(focus_token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus_token]);

  useEffect(() => {
    if (stop_open && stop_ref.current) stop_ref.current.focus({ preventScroll: true });
    if (stop_open || !return_focus.current) return;
    const target = return_focus.current === 'stop' ? stop_button_ref.current : heading_ref.current;
    return_focus.current = null;
    if (target) target.focus({ preventScroll: true });
  }, [stop_open]);

  // Focus moves into the confirm on open, and back to Resume (else the heading) once it closes, as the Stop form does:
  // the Resume button is only rendered again after the confirm has unmounted.
  const takeover_return = useRef(false);
  useEffect(() => {
    if (takeover_open) {
      takeover_return.current = true;
      if (takeover_ref.current) takeover_ref.current.focus({ preventScroll: true });
      return;
    }
    if (!takeover_return.current) return;
    takeover_return.current = false;
    const target = resume_ref.current || heading_ref.current;
    if (target) target.focus({ preventScroll: true });
  }, [takeover_open]);

  // A takeover confirm left open once the server no longer offers it has nothing left to confirm.
  useEffect(() => {
    if (!run.resume_takes_over) setTakeoverOpen(false);
  }, [run.resume_takes_over]);

  const close_takeover = () => setTakeoverOpen(false);

  const press_resume = () => {
    if (run.resume_takes_over) setTakeoverOpen(true);
    else on_resume(false);
  };

  // A stop form left open while the run ends (or someone else stopped it) has nothing left to stop.
  useEffect(() => {
    if (!run.can_cancel) setStopOpen(false);
  }, [run.can_cancel]);

  const stop_error = reason_error(stop_reason);

  const confirm_stop = () => {
    setStopTouched(true);
    if (stop_error || action_busy) return;
    on_cancel(stop_reason).then((ok) => {
      if (!ok) return;
      return_focus.current = 'heading';
      setStopOpen(false);
      setStopReason('');
      setStopTouched(false);
    });
  };

  const retry = () => {
    if (retry_busy) return;
    setRetryBusy(true);
    setRetryText('');
    on_retry()
      .then((why_not) => setRetryText(why_not || ''))
      .catch(() => setRetryText('Could not load the teachers that need attention. Try again.'))
      .finally(() => setRetryBusy(false));
  };

  const show_retry = run.can_retry;
  const busy = !!action_busy;

  return (
    <section className="tp-card" aria-labelledby="tp-run-heading" data-testid="tp-run-panel">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-run-heading" ref={heading_ref} tabIndex={-1}>
          {run.title} · {run.month_label}
        </span>
        <StatusPill tone={run.status_tone}>{run.status_label}</StatusPill>
      </div>
      <div className="tp-form">
        <progress
          className="tp-run-progress"
          max={Math.max(run.counts.total, 1)}
          value={run.done}
          aria-label={`${run.title} progress`}
          aria-valuetext={run.progress_text}
        />
        <div className="sr-only" role="status" aria-live="polite">
          {run.announce_text}
        </div>
        <div className="tp-na-headline">{run.headline}</div>
        <div className="tp-card-hd-sub">
          {[run.started_text, run.finished_text, run.current_text].filter(Boolean).join(' · ')}
        </div>
        {run.taken_over_text ? <div className="tp-card-hd-sub">{run.taken_over_text}</div> : null}
        {run.skipped_text ? <div className="tp-card-hd-sub">{run.skipped_text}</div> : null}
        {run.reason ? (
          <div className="tp-card-hd-sub">
            Reason: {run.reason}
            {run.override_used ? ' · with an override reason' : ''}
          </div>
        ) : null}

        {run.pause_text ? (
          <div
            className={`tp-banner tp-banner-${run.paused_reason === 'SWITCH_OFF' ? 'warn' : 'error'}`}
          >
            {run.pause_text}
          </div>
        ) : null}
        {run.cancel_text ? <div className="tp-banner tp-banner-warn">{run.cancel_text}</div> : null}
        {run.resume_hint ? <div className="tp-card-hd-sub">{run.resume_hint}</div> : null}
        {run.resume_takes_over_text ? (
          <div className="tp-card-hd-sub" id="tp-run-takeover-note">
            {run.resume_takes_over_text}
          </div>
        ) : null}
        {poll_failed ? (
          <div className="tp-card-hd-sub" role="status">
            Could not refresh the progress; trying again.
          </div>
        ) : null}
        {action_error ? (
          <div className="tp-neg" role="alert">
            {action_error}
          </div>
        ) : null}

        <div className="tp-frow">
          {run.can_resume && !takeover_open ? (
            <button
              type="button"
              className="tp-btn"
              ref={resume_ref}
              onClick={busy ? undefined : press_resume}
              aria-disabled={busy ? 'true' : undefined}
              aria-describedby={run.resume_takes_over ? 'tp-run-takeover-note' : undefined}
            >
              {action_busy === 'resume'
                ? 'Resuming…'
                : run.resume_takes_over
                  ? 'Resume as you'
                  : 'Resume'}
            </button>
          ) : null}
          {run.can_cancel && !stop_open ? (
            <button
              type="button"
              className="tp-btn-out"
              ref={stop_button_ref}
              onClick={() => setStopOpen(true)}
            >
              Stop run
            </button>
          ) : null}
          {show_retry ? (
            <button
              type="button"
              className="tp-btn-out"
              onClick={retry_busy ? undefined : retry}
              aria-disabled={retry_busy ? 'true' : undefined}
            >
              {retry_busy ? 'Loading…' : 'Retry the teachers that failed'}
            </button>
          ) : null}
          {run.terminal ? (
            <button type="button" className="tp-btn-ghost" onClick={on_dismiss}>
              Hide
            </button>
          ) : null}
        </div>
        {retry_text ? (
          <div className="tp-card-hd-sub" role="status">
            {retry_text}
          </div>
        ) : null}

        {takeover_open ? (
          <div
            className="tp-banner tp-banner-warn"
            role="group"
            aria-label="Resume this run as you"
            onKeyDown={(e) => {
              if (e.key === 'Escape') close_takeover();
            }}
          >
            <div>{takeover_confirm_text(run)}</div>
            <div className="tp-modal-ft">
              <button type="button" className="tp-btn-ghost" onClick={close_takeover}>
                Keep paused
              </button>
              <button
                type="button"
                className="tp-btn"
                ref={takeover_ref}
                onClick={
                  busy
                    ? undefined
                    : () => {
                        setTakeoverOpen(false);
                        on_resume(true);
                      }
                }
                aria-disabled={busy ? 'true' : undefined}
              >
                {action_busy === 'resume' ? 'Resuming…' : 'Resume as you'}
              </button>
            </div>
          </div>
        ) : null}
        {stop_open ? (
          <div className="tp-banner tp-banner-warn" role="group" aria-label="Stop this run">
            <div>
              The teacher in progress finishes; the rest are not attempted and can be closed by a
              new run. What is already done stays done.
            </div>
            <div className="tp-field">
              <label htmlFor="tp-run-stop-reason">
                Reason (required, recorded in the audit trail)
              </label>
              <textarea
                id="tp-run-stop-reason"
                ref={stop_ref}
                className="tp-textarea"
                value={stop_reason}
                onChange={(e) => setStopReason(e.target.value)}
                onBlur={() => setStopTouched(true)}
                aria-invalid={stop_touched && stop_error ? 'true' : undefined}
                aria-describedby={stop_touched && stop_error ? 'tp-run-stop-error' : undefined}
              />
            </div>
            {stop_touched && stop_error ? (
              <div className="tp-neg" role="alert" id="tp-run-stop-error">
                {stop_error}
              </div>
            ) : null}
            <div className="tp-modal-ft">
              <button
                type="button"
                className="tp-btn-ghost"
                onClick={
                  busy
                    ? undefined
                    : () => {
                        return_focus.current = 'stop';
                        setStopOpen(false);
                      }
                }
                aria-disabled={busy ? 'true' : undefined}
              >
                Keep running
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-danger"
                onClick={busy ? undefined : confirm_stop}
                aria-disabled={busy ? 'true' : undefined}
              >
                {action_busy === 'cancel' ? 'Stopping…' : 'Stop the run'}
              </button>
            </div>
          </div>
        ) : null}

        <RunItems
          run={run}
          bucket={bucket}
          offset={offset}
          loading={items_loading}
          on_bucket={on_bucket}
          on_page={on_page}
          on_view_issues={on_view_issues}
        />
      </div>
    </section>
  );
};

export default RunPanel;
